import { createAsyncThunk } from "@reduxjs/toolkit";

import { mapResponseToCompanySearch } from "../../../api/mapper";
import * as RegistrationApi from "../../../api/registrationApi";
import { trackEvent } from "../../../utils/tracking";
import { TRACKING_EVENTS } from "../../../utils/trackingDefinitions";
import { RegisterBusinessPayload } from "./register.types";

export const delay = (time) =>
	new Promise((resolve) => setTimeout(resolve, time));

export const validatePostcodeLookup = createAsyncThunk(
	"register/validatePostcodeLookup",
	async ({
		postcode,
		countryCode,
		form,
		target,
	}: {
		postcode: string;
		countryCode: string;
		form: string;
		target: string;
	}) => {
		try {
			const response = await RegistrationApi.validatePostcodeLookup({
				postcode,
				countryCode,
				form,
				target,
			});

			return {
				isSuccess: response.isSuccess,
				postcode,
				form,
				target,
				result: response.data,
				error: response.errors?.[0],
			};
		} catch (error) {
			return { error };
		}
	},
);

export const searchCompanyData = createAsyncThunk(
	"register/b2b/searchCompanyData",
	async ({
		country,
		query,
		companyType,
	}: {
		country: string;
		query: string;
		companyType: string;
	}) => {
		try {
			const response = await RegistrationApi.searchCompanyData({
				country,
				query,
				companyType,
			});

			if (response.isSuccess) {
				trackEvent({
					event: TRACKING_EVENTS.REGISTRATION.EVENT.USER_INTERACTION,
					target: TRACKING_EVENTS.REGISTRATION.APP,
					value: "search_organization_number",
					key: "action",
				});
			} else {
				trackEvent({
					event: TRACKING_EVENTS.REGISTRATION.EVENT.USER_INTERACTION,
					target: TRACKING_EVENTS.REGISTRATION.APP,
					value: "search_organization_number_failed",
					key: "action",
				});
			}

			return {
				isSuccess: response.isSuccess,
				query,
				error: response.errors?.[0],
				result: mapResponseToCompanySearch(response.data, query),
			};
		} catch (error) {
			return { query, error };
		}
	},
);

export const searchCompanyDetails = createAsyncThunk(
	"register/b2b/searchCompanyDetails",
	async ({ lookupId }: { lookupId: string }) => {
		try {
			const response = await RegistrationApi.searchCompanyDetails({ lookupId });

			if (response.isSuccess) {
				trackEvent({
					event: TRACKING_EVENTS.REGISTRATION.EVENT.USER_INTERACTION,
					target: TRACKING_EVENTS.REGISTRATION.APP,
					value: "select_organization_number",
				});
			} else {
				trackEvent({
					event: TRACKING_EVENTS.REGISTRATION.EVENT.USER_INTERACTION,
					target: TRACKING_EVENTS.REGISTRATION.APP,
					value: "select_organization_number_failed",
				});
			}

			const result = {
				...response?.data,
			};

			return {
				isSuccess: response.isSuccess,
				lookupId,
				result,
				error: response.errors?.[0],
			};
		} catch (error) {
			return { lookupId, error };
		}
	},
);

export const searchCompanySubUnits = createAsyncThunk(
	"register/b2b/searchCompanySubUnits",
	async ({ canonicalId, query }: { canonicalId: string; query: string }) => {
		try {
			const response = await RegistrationApi.searchCompanySubUnits({
				canonicalId,
				query,
			});

			if (response.isSuccess) {
				trackEvent({
					event: TRACKING_EVENTS.REGISTRATION.EVENT.USER_INTERACTION,
					target: TRACKING_EVENTS.REGISTRATION.APP,
					value: "search_subunit_organization_number",
					key: "action",
				});
			} else {
				trackEvent({
					event: TRACKING_EVENTS.REGISTRATION.EVENT.USER_INTERACTION,
					target: TRACKING_EVENTS.REGISTRATION.APP,
					value: "search_subunit_organization_number_failed",
					key: "action",
				});
			}

			return {
				isSuccess: response.isSuccess,
				canonicalId,
				result: mapResponseToCompanySearch(response.data),
				error: response.errors?.[0],
			};
		} catch (error) {
			return { error };
		}
	},
);

export const checkIfUsernameExists = createAsyncThunk(
	"register/checkIfUsernameExists",
	async (data: { form: string; username: string }) => {
		try {
			const response = await RegistrationApi.checkIfUsernameExists(data);

			if (!response.isSuccess) {
				trackEvent({
					event: TRACKING_EVENTS.COMMON.EVENT.USER_INTERACTION,
					target: TRACKING_EVENTS.REGISTRATION.APP,
					key: "action",
					value: TRACKING_EVENTS.COMMON.EVENT.USERNAME_NOT_EXISTS,
				});
			} else {
				if (response.userExists) {
					trackEvent({
						event: TRACKING_EVENTS.COMMON.EVENT.USER_INTERACTION,
						target: TRACKING_EVENTS.REGISTRATION.APP,
						key: "action",
						value: TRACKING_EVENTS.COMMON.EVENT.USERNAME_EXISTS,
					});
				}
			}

			return {
				isSuccess: response.isSuccess,
				form: data.form,
				username: data.username,
				error: response.errors?.[0],
				result: response,
			};
		} catch (error) {
			return { error };
		}
	},
);

export const registerUser = createAsyncThunk(
	"register/b2c/registerUser",
	async (data: {
		consents: {
			clubAgreement: { consented: boolean };
		};
	}) => {
		try {
			const response = await RegistrationApi.registerUser(data);

			if (!response.isSuccess) {
				trackEvent({
					event: TRACKING_EVENTS.REGISTRATION.EVENT.REGISTRATION_FAILED,
					target: TRACKING_EVENTS.REGISTRATION.APP,
				});
			} else {
				if (data.consents?.clubAgreement?.consented) {
					trackEvent({
						event:
							TRACKING_EVENTS.REGISTRATION.EVENT.LOYALTY_REGISTRATION_COMPLETED,
						target: TRACKING_EVENTS.REGISTRATION.APP,
					});

					trackEvent({
						event: TRACKING_EVENTS.REGISTRATION.EVENT.REGISTRATION_COMPLETED,
						target: TRACKING_EVENTS.REGISTRATION.APP,
					});
				} else {
					trackEvent({
						event: TRACKING_EVENTS.REGISTRATION.EVENT.REGISTRATION_COMPLETED,
						target: TRACKING_EVENTS.REGISTRATION.APP,
					});
				}
			}

			window.location.href = response.returnUrl;

			return {
				isSuccess: response.isSuccess,
				error: response.errors?.[0],
			};
		} catch (error) {
			trackEvent({
				event: TRACKING_EVENTS.REGISTRATION.EVENT.REGISTRATION_FAILED,
				target: TRACKING_EVENTS.REGISTRATION.APP,
			});

			return { error: "komid.general.network.error" };
		}
	},
);

export const registerBusiness = createAsyncThunk(
	"register/b2b/registerBusiness",
	async (data: RegisterBusinessPayload) => {
		try {
			const response = await RegistrationApi.registerBusiness(data);

			if (!response.isSuccess) {
				const error = getRegistrationFailureReason(response.data);

				trackEvent({
					event: TRACKING_EVENTS.REGISTRATION.EVENT.REGISTRATION_FAILED,
					target: TRACKING_EVENTS.REGISTRATION.APP,
					value: error,
				});

				return {
					...data,
					error: error || response.errors?.[0],
				};
			}

			if (data.verify) {
				trackEvent({
					event: TRACKING_EVENTS.REGISTRATION.EVENT.USER_INTERACTION,
					target: TRACKING_EVENTS.REGISTRATION.APP,
					key: "action",
					value:
						TRACKING_EVENTS.REGISTRATION.EVENT
							.REGISTER_WITH_PROFILE_VERIFICATION,
				});
			} else {
				trackEvent({
					event: TRACKING_EVENTS.REGISTRATION.EVENT.USER_INTERACTION,
					target: TRACKING_EVENTS.REGISTRATION.APP,
					key: "action",
					value: TRACKING_EVENTS.REGISTRATION.EVENT.SKIP_PROFILE_VERIFICATION,
				});
			}

			if (data.consents?.clubAgreement?.consented) {
				trackEvent({
					event:
						TRACKING_EVENTS.REGISTRATION.EVENT.LOYALTY_REGISTRATION_COMPLETED,
					target: TRACKING_EVENTS.REGISTRATION.APP,
				});

				trackEvent({
					event: TRACKING_EVENTS.REGISTRATION.EVENT.REGISTRATION_COMPLETED,
					target: TRACKING_EVENTS.REGISTRATION.APP,
				});
			} else {
				trackEvent({
					event: TRACKING_EVENTS.REGISTRATION.EVENT.REGISTRATION_COMPLETED,
					target: TRACKING_EVENTS.REGISTRATION.APP,
				});
			}

			let redirect = response.returnUrl;
			if (response.data?.isAccountBlockedAwaitingApproval) {
				if (response.data?.verificationUrl) {
					redirect = response.data.verificationUrl;
				} else {
					redirect = `${response.returnUrl}&isAccountBlockedAwaitingApproval=true`;
				}
			}

			window.location.href = redirect;

			return { isSuccess: response.isSuccess };
		} catch (error) {
			trackEvent({
				event: TRACKING_EVENTS.REGISTRATION.EVENT.REGISTRATION_FAILED,
				target: TRACKING_EVENTS.REGISTRATION.APP,
			});

			return { ...data, error: "komid.general.network.error" };
		}
	},
);

const getRegistrationFailureReason = (errors) => {
	if (errors) {
		const [prefix] = Object.entries(errors).find(
			([, errorValue]) => errorValue,
		);

		return `komid.register.error.${prefix}`;
	}

	return null;
};

export const getProfileData = createAsyncThunk(
	"register/b2b/getProfileData",
	async (returnUrl?: string) => {
		try {
			const response = await RegistrationApi.getProfileData(returnUrl);

			return {
				isSuccess: response.isSuccess,
				error: response.errors?.[0],
				result: response.data,
			};
		} catch (error) {
			return { error };
		}
	},
);

export const verifyProfile = createAsyncThunk(
	"register/b2b/verifyProfile",
	async (data: {
		id: string;
		username: string;
		role: string;
		password: string;
		returnUrl: string;
		consents?: {
			clubAgreement?: {
				consented: boolean;
				consentText: string;
			};
			clubSms?: {
				consented: boolean;
				consentText: string;
			};
			newsletterEmail?: {
				consented: boolean;
				consentText: string;
			};
		};
		verify: boolean;
	}) => {
		try {
			const response = await RegistrationApi.verifyProfile(data);

			if (response.isSuccess) {
				if (data.verify) {
					trackEvent({
						event: TRACKING_EVENTS.REGISTRATION.EVENT.USER_INTERACTION,
						target: TRACKING_EVENTS.REGISTRATION.APP,
						key: "action",
						value:
							TRACKING_EVENTS.REGISTRATION.EVENT.PROFILE_VERIFICATION_COMPLETED,
					});
				} else {
					trackEvent({
						event: TRACKING_EVENTS.REGISTRATION.EVENT.USER_INTERACTION,
						target: TRACKING_EVENTS.REGISTRATION.APP,
						key: "action",
						value: TRACKING_EVENTS.REGISTRATION.EVENT.SKIP_PROFILE_VERIFICATION,
					});
				}
			} else {
				trackEvent({
					event: TRACKING_EVENTS.REGISTRATION.EVENT.USER_INTERACTION,
					target: TRACKING_EVENTS.REGISTRATION.APP,
					key: "action",
					value: TRACKING_EVENTS.REGISTRATION.EVENT.PROFILE_VERIFICATION_FAILED,
				});
			}

			return {
				isSuccess: response.isSuccess,
				error: response.errors?.[0],
				returnUrl: response.returnUrl,
			};
		} catch (error) {
			return { error };
		}
	},
);
